import React from "react";
import { useState } from "react";
import { Backdrop, Button, Chip, FormFieldSet, LogoLoader, Modal, NewTableAdv, showToast, Skeleton, StatusTags, TableAdv } from "../../../../components";
import { Link } from "react-router-dom";
import { mapTableRows } from "../../../../utils";
import { updateSubmissionStatus, updateTalentMatchStatus } from "../../../../redux/slices/bdmjobs/bdmjobsApi";
import { useSelector } from "react-redux";
import ApplicantsDetailsDrawer from "../../Applicants/Applicant/Modal/ApplicantsDetailsDrawer";
import { formatPhoneNumberLength, formatWords2 } from "../../../../utils/stringUtils";

const JobsAccordion = ({ rowData, type, loading = false, sourceFrom, onStatusUpdate }) => {
  const { user } = useSelector((state) => state.auth);
  const [detailsDrawer, setDetailsDrawer] = useState(false);
  const [selectedId, setSelectedId] = useState(null)
  const [statusLoading, setStatusLoading] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedStatusData, setSelectedStatusData] = useState(null);
  const [interviewDate, setInterviewDate] = useState(null);
  const [interviewDateError, setInterviewDateError] = useState("");

  const INTERVIEW_STATUS_IDS = [5,12, 13, 14, 15, 16, 17, 24, 25, 26, 34, 36, 40, 55, 56];
  // Rejected statuses that should NOT open a modal
  const REJECTED_STATUS_IDS = [21, 22, 23, 30, 32, 35, 38, 45, 46, 47, 48, 49, 57, 58, 62, 74, 109]; // 109 is Rejected Internally
  const ApplicantSubStatus = {
    1: "Shortlisted",
    2: "Pipeline",
    3: "Submitted",
    4: "Review call by BDM",
    5: "BDM Interview",
    6: "Disqualified",
    7: "Hold",
    8: "Withdrawn",
    
    9: "Waiting for Writeup",
    10: "Submitted to Client",
    11: "Submitted To Vendor",
    12: "Shortlisted for Interview",
    13: "Interview Confirmation",
    14: "Interview Scheduled",
    15: "L1 Interview Scheduled",
    16: "L2 Interview Scheduled",
    17: "L3 Interview Scheduled",
    18: "L1 Interview Selected",
    19: "L2 Interview Selected",
    20: "L3 Interview Selected",
    21: "L1 Interview Rejected",
    22: "L2 Interview Rejected",
    23: "L3 Interview Rejected",

    24: "L1 Interview Re-Scheduled",
    25: "L2 Interview Re-Scheduled",
    26: "L3 Interview Re-Scheduled",

    27: "L1 Interview Feedback Pending",
    28: "L2 Interview Feedback Pending",
    29: "L3 Interview Feedback Pending",

    30: "Rejected By Client",
    31: "Selected but no response from the client",
    32: "Rejected after Client Selection",

    33: "Submitted to End Client",

    34: "Vendor Interview",
    35: "Vendor Interview Reject",

    36: "Client Interview",
    37: "Client Interview Select",
    38: "Client Interview Reject",
    39: "Client Interview Completed",

    40: "End Client Interview",
    41: "End Client Interview Completed",

    42: "Selected By Vendor",
    43: "Selected By Client",
    44: "Selected By End Client",

    45: "Rejected By Vendor",
    46: "Rejected by End Client",

    47: "Disqualified By Vendor",
    48: "Disqualified By Client",
    49: "Disqualified by End Client",

    50: "Hold By Vendor",
    51: "Hold By Client",
    52: "Hold by End Client",

    53: "Drop",
    54: "Position on Hold",

    55: "PW Interview Proposed By Buyer",
    56: "PW Interview Proposed By Supplier",
    57: "PW Interview Failed",
    58: "PW Interview Cancelled",
    59: "PW Interview Success",

    60: "Assignment Contract Sent",
    61: "Assignment Contract Verified",
    62: "Assignment Contract Rejected",

    63: "Confirmed",
    64: "Position Filled",
    65: "Backout",
    66: "Not Joined",
    67: "BGV Failed",
    68: "Got Pay Rate Confirmation",
    69: "Offer Withdrawn",
    70: "Negotiation",
    71: "Start",
    72: "Offer Sent",
    73: "Offer Accepted",
    74: "Offer Rejected",
    75: "Hold with Client",
    76: "Hold with Vendor",
    77: "Rejected Internally",
  };
  const TalentMatchStatus = {
    1: "Talent Match",
    2: "Shortlist"
  }

  // const SourceMap = {
  //   1: "Internal DB",
  //   2: "Career Builder",
  //   3: "Monster",
  // };
  const SourceMap = {
    1: "Internal DB",
    2: "Career Builder",
    3: "Monster",
    4: "Career Portal",
    5: "CareerJet",
    6: "Dice",
    7: "Glassdoor",
    8: "Indeed",
    9: "LinkedIn",
    10: "Naukari",
    11: "Neuvoo",
    12: "Post Job Free",
    13: "Snaprecruit",
    14: "Stack Overflow",
    15: "ZipRecruiter",
    16: "Employee Referral",
    17: "Employment Agency",
    18: "Gmail",
    19: "Job Fair",
    20: "Others",
    21: "Outlook",
    22: "Passive Sourcing",
    23: "Resume Inbox"
  };

  const ApplicantSubStatusOptions = Object.entries(ApplicantSubStatus).map(
    ([value, label]) => ({
      value: Number(value),
      label,
    })
  );
  const TalentMatchStatusOptions = Object.entries(TalentMatchStatus).map(
    ([value, label]) => ({
      value: Number(value),
      label,
    })
  );

  const columns = [
    {
      key: "submissionId",
      label: "Submission ID",
    },
    {
      key: "applicantName",
      label: "Applicant Name",
      render: (row) => (
        <Link
          to={`/ats/applicants/applicant-details`}
          state={{ applicantId: row.candidateId, source: sourceFrom ?? "bdm-alljobs" }}
          className="text-info-400 flex items-center gap-.5"
        >
          <span className="mx-2">{formatWords2(row.applicantName)}</span>
        </Link>
      ),
    },
    {
      key: "workAuth",
      label: "Work Authorization",
    },
    {
      key: "mobile",
      label: "Mobile Number",
    },
    {
      key: "applicantLocation",
      label: "Location",
    },
    {
      key: "country",
      label: "Country",
    },
    {
      key: "experience",
      label: "Experience",
    },
    {
      key: "source",
      label: "Source",
      render: (row) => {
        const sourceText = SourceMap[row.source] || row.source || "Unknown";
        return <span>{sourceText}</span>;
      },
    },
    {
      key: "resume",
      label: "Resume",
    },
    {
      key: "revisionStatus",
      label: "Revision Status",
      render: (row) => <StatusTags tag={row.revisionStatus} variant="text" />,
    },
    {
      key: "applicationStatus",
      label: "Application Status",
      render: (row) => {
        // Determine which dropdown options to show
        const isTalentMatch = type === "applications" || row.source === "Talent Match";
        //console.log("isTalentMatch",isTalentMatch)
        const options = isTalentMatch
          ? TalentMatchStatusOptions
          : ApplicantSubStatusOptions;

        const lastStatusEntry = row.applicationStatus?.[row.applicationStatus.length - 1];
        const statusNumber = lastStatusEntry?.status || (isTalentMatch ? 0 : 3);
        const currentOption =
          options.find((opt) => opt.value === statusNumber) || options[0];

        return (
          <div className="w-[220px]">
            <FormFieldSet
              type="select"
              noTitle
              size="sm"
              options={options}
              value={currentOption}
              showSearch
              onChange={(option) =>
                handleUpdateSubmissionStatus(option, row, isTalentMatch)
              }
            />
          </div>
        );
      },
    },

    {
      key: "billRate",
      label: "Bill Rate",
    },
    {
      key: "payRate",
      label: "Pay Rate",
    },
    {
      key: "employerName",
      label: "Employer Name",
    },
    {
      key: "availability",
      label: "Availability",
    },
    {
      key: "submittedBy",
      label: "Submitted By",
    },
    {
      key: "pwSubmissionType",
      label: "PW Submission Type",
    },
    {
      key: "noticePeriod",
      label: "Notice Period",
    },
    {
      key: "currentCTC",
      label: "Current CTC",
    },
    {
      key: "submittedOn",
      label: "Submitted On",
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div className="flex items-center">
          <Button
            type="icon"
            variant="text"
            icon={"visibility"}
            onClick={() => {
              setSelectedId(row?.candidateId)
              setDetailsDrawer(true);
            }}
            color="black"
            size="sm"
          />
          <Link
            to={`/ats/applicants/applicant-details`}
            state={{ applicantId: row?.candidateId, source: sourceFrom == "draftJobs" ? "draftJobs-edit" : "bdm-alljobs-edit" }}
            className="text-info-400 flex items-center gap-.5"
          >
            <Button
              type="icon"
              variant="text"
              icon={"edit_square"}
              color="black"
              size="sm"
            />
          </Link>

          <Button
            type="icon"
            variant="text"
            icon={"delete"}
            color="black"
            size="sm"
          />
        </div>
      ),
    },
  ];


  const applyStatusStatusChange = async (option, row, isTalentMatch, scheduledDate = null) => {
    try {
      setStatusLoading(true);
      const newStatus = option;
      const history = row?.applicationStatus || [];
      const currentStatus = history.length > 0 ? history[history.length - 1].status : null;

      if (currentStatus === newStatus && !scheduledDate) {
        showToast("Status already set to this value", "info");
        return;
      }

      const payload = [
        {
          type: "PUSH",
          value: {
            statusHistory: {
              addedBy: user?.data?.uiid,
              addedOn: new Date().toISOString(),
              status: newStatus,
              ...(scheduledDate && { scheduledDate: new Date(scheduledDate).toISOString() }),
            },
          },
        },
      ];

      // const coll = isTalentMatch ? "TalentMatche" : "Submission";
      // const res = await updateSubmissionStatus({
      //   // coll: "Submission",
      //   coll,
      //   uiid: row.submissionId,
      //   payload,
      // });
      const apiFn = isTalentMatch ? updateTalentMatchStatus : updateSubmissionStatus;
      const coll = isTalentMatch ? "TalentMatche" : "Submission";

      const res = await apiFn({
        coll,
        uiid: row.submissionId,
        payload,
      });

      if (res?.data) {
        // showToast("Submission status updated", "success");
        showToast(
          `${isTalentMatch ? "Talent Match" : "Submission"} status updated`,
          "success"
        );
        // const updatedHistory = res?.data?.data?.doc?.statusHistory;
        // const lastHistory = updatedHistory[updatedHistory?.length - 1];
        // row?.applicationStatus?.push(lastHistory);
        // const updatedRows = rows.map((r) =>
        //   r.submissionId === row.submissionId
        //     ? {
        //         ...r,
        //         applicationStatus: [
        //           ...(r.applicationStatus || []),
        //           {
        //             addedOn: new Date().toISOString(),
        //             status: newStatus,
        //           },
        //         ],
        //       }
        //     : r
        // );
        // rows = updatedRows;
        row.applicationStatus = [
          ...history,
          {
            status: newStatus,
            ...(scheduledDate && { scheduledDate: new Date(scheduledDate).toISOString() }),
            addedBy: { uiid: user?.data?.uiid },
            addedOn: new Date().toISOString(),
          },
        ];

        // Notify parent component about the status change
        if (onStatusUpdate) {
          onStatusUpdate({
            submissionId: row.submissionId,
            candidateId: row.candidateId,
            newStatus,
            isTalentMatch,
            type
          });
        }
      } else {
        showToast("Failed to update", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Failed to update", "error");
    } finally {
      setStatusLoading(false);
    }
  };

  const handleUpdateSubmissionStatus = async (option, row, isTalentMatch) => {
    const newStatus = Number(typeof option === "object" ? option.value : option);
    const history = row?.applicationStatus || [];
    const currentStatus = history.length > 0 ? history[history.length - 1].status : null;

    if (currentStatus === newStatus) {
      showToast("Status already set to this value", "info");
      return;
    }

    // Check if it's an interview status and NOT a rejected status
    if (INTERVIEW_STATUS_IDS.includes(newStatus) && !REJECTED_STATUS_IDS.includes(newStatus)) {
      setSelectedStatusData({ option: newStatus, row, isTalentMatch });
      setInterviewDate(null);
      setInterviewDateError("");
      setIsStatusModalOpen(true);
      return;
    }

    await applyStatusStatusChange(newStatus, row, isTalentMatch);
  };

  const resetStatusModal = () => {
    setIsStatusModalOpen(false);
    setSelectedStatusData(null);
    setInterviewDate(null);
    setInterviewDateError("");
  };

  const validateInterviewDate = (date, newStatus, statusHistory = []) => {
    if (!date) return "Interview date is required.";

    const selected = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selected < today) return "Interview date cannot be in the past.";

    const getLastDateForStatus = (status) => {
      const entries = (statusHistory || []).filter((h) => h?.status === status);
      if (!entries.length) return null;
      // Use scheduledDate if available, otherwise fallback to addedOn
      const lastEntry = entries[entries.length - 1];
      return new Date(lastEntry?.scheduledDate || lastEntry?.addedOn);
    };

    // Validation for Round 2 (ID 16 and 25)
    if (newStatus === 16 || newStatus === 25) {
      const l1Date = getLastDateForStatus(15) || getLastDateForStatus(24);
      if (!l1Date) return "L1 Interview must be scheduled before L2.";
      if (selected <= l1Date)
        return "L2 Interview must be scheduled after L1 Interview.";
    }

    // Validation for Round 3 (ID 17 and 26)
    if (newStatus === 17 || newStatus === 26) {
      const l2Date = getLastDateForStatus(16) || getLastDateForStatus(25);
      if (!l2Date) return "L2 Interview must be scheduled before L3.";
      if (selected <= l2Date)
        return "L3 Interview must be scheduled after L2 Interview.";
    }

    return "";
  };

  const handleStatusConfirm = async () => {
    if (selectedStatusData) {
      const { option, row } = selectedStatusData;
      const error = validateInterviewDate(interviewDate, option, row?.applicationStatus);
      if (error) { 
        setInterviewDateError(error);
        showToast(error, "error");
        return;
      }
      const { isTalentMatch } = selectedStatusData;
      await applyStatusStatusChange(option, row, isTalentMatch, interviewDate);
    }
  };

  const newRowData = rowData?.map(data => ({
    ...data,
    mobile: data?.mobile?.replace(/\D/g, "")?.length
      ? formatPhoneNumberLength("+1", data?.mobile)
      : data?.mobile
  }));
  const rows = rowData?.length ? newRowData : [];
  const mappedRows = mapTableRows(rows, columns);
  const padding = "p-4";
  const tableConfigs = {
    applications: {
      name: "Applications",
      columns: columns,
      rows: mappedRows,
    },
    shortlisted: {
      name: "Shortlisted",
      columns: columns,
      rows: mappedRows,
    },
    pipelined: {
      name: "Pipelined",
      columns: columns,
      rows: mappedRows,
    },
    submitted: {
      name: "Submitted",
      columns: columns,
      // rows: submittedRows,
      rows: mappedRows,
    },
  };

  const config = tableConfigs[type];

  return (
    config && (
      <div className={`${padding}`}>
        <h6 className="m-1">
          {config.name} : {config.rows?.length ?? 0}
        </h6>
        {/* <NewTableAdv columns={config.columns} rows={config.rows} hideToolBar /> */}
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <LogoLoader />
          </div>
        ) : (
          <TableAdv columns={columns} rows={config.rows} hideToolBar />
        )}
        <ApplicantsDetailsDrawer
          isOpen={detailsDrawer}
          onClose={() => setDetailsDrawer(false)}
          applicantId={selectedId}
        />
        <Backdrop zIndex={53} isOpen={statusLoading}>
          <LogoLoader />
        </Backdrop>

        {/* Interview Date Modal */}
        <Modal
          isOpen={isStatusModalOpen}
          onClose={resetStatusModal}
          title={"Update Application Status"}
          titleColor={"primary"}
          width="max-w-sm"
        >
          <div className="flex flex-col gap-6 p-2">
            <p className="text-sm text-gray-500">
              Please select the interview date to proceed.
            </p>
            <FormFieldSet
              type="datepicker"
              title={
                selectedStatusData?.option === 5 ? "BDM Interview Date" :
                selectedStatusData?.option === 15 || selectedStatusData?.option === 24 ? "Interview L1 Date" :
                selectedStatusData?.option === 16 || selectedStatusData?.option === 25 ? "Interview L2 Date" :
                selectedStatusData?.option === 17 || selectedStatusData?.option === 26 ? "Interview L3 Date" :
                selectedStatusData?.option === 34 ? "Vendor Interview Date" :
                selectedStatusData?.option === 36 ? "Client Interview Date" :
                selectedStatusData?.option === 40 ? "End Client Interview Date" :
                "Interview Date"
              }
              placeholder="Select Date"
              value={interviewDate}
              minDate={new Date()}
              onChange={(value) => {
                setInterviewDate(value);
                if (interviewDateError) {
                  setInterviewDateError(
                    validateInterviewDate(
                      value,
                      selectedStatusData?.option,
                      selectedStatusData?.row?.applicationStatus
                    )
                  );
                }
              }}
              error={interviewDateError}
            />
            {selectedStatusData?.option === 16 &&
              (() => {
                const l1Entry = [...(selectedStatusData?.row?.applicationStatus || [])]
                  .reverse()
                  .find((h) => h?.status === 15);
                return l1Entry?.scheduledDate ? (
                  <p className="text-xs text-info-500 -mt-4">
                    L1 was scheduled on{" "}
                    <span className="font-semibold">
                      {new Date(l1Entry.scheduledDate).toLocaleDateString("en-US", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                    . L2 must be after this date.
                  </p>
                ) : null;
              })()}
            {selectedStatusData?.option === 17 &&
              (() => {
                const l2Entry = [...(selectedStatusData?.row?.applicationStatus || [])]
                  .reverse()
                  .find((h) => h?.status === 16);
                return l2Entry?.scheduledDate ? (
                  <p className="text-xs text-info-500 -mt-4">
                    L2 was scheduled on{" "}
                    <span className="font-semibold">
                      {new Date(l2Entry.scheduledDate).toLocaleDateString("en-US", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                    . L3 must be after this date.
                  </p>
                ) : null;
              })()}
            <div className="flex justify-end gap-3 mt-2">
              <Button
                variant="outlined"
                title="Cancel"
                color="black"
                disabled={statusLoading}
                onClick={resetStatusModal}
              />
              <Button
                title={statusLoading ? "Submitting..." : "Submit"}
                disabled={statusLoading}
                onClick={handleStatusConfirm}
              />
            </div>
          </div>
        </Modal>
      </div>
    )
  );
};

export default JobsAccordion;
